import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import usePolling from '../hooks/usePolling';
import ChatBubble from '../components/ChatBubble';
import SlotPanel from '../components/SlotPanel';
import StatusBadge from '../components/StatusBadge';

function iniciais(nome) {
  if (!nome) return '?';
  const parts = nome.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const ACAO = {
  urgente: { bg: '#FDE8E8', color: '#991B1B', icon: '⚠️', texto: 'Urgência detectada — atendimento imediato' },
  pendente: { bg: '#FAEEDA', color: '#854F0B', icon: '📋', texto: 'Dados completos — lançar na agenda' },
  agendada: { bg: '#E1F5EE', color: '#0F6E56', icon: '📅', texto: 'Consulta confirmada' },
};

export default function Conversa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conversa, setConversa] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [assumindo, setAssumindo] = useState(false);
  const bottomRef = useRef(null);

  const fetchTudo = useCallback(async () => {
    try {
      const [{ data: conv }, { data: msgs }] = await Promise.all([
        api.get(`/conversas/${id}`),
        api.get(`/conversas/${id}/mensagens`),
      ]);
      setConversa(conv.data);
      setMensagens(msgs.data ?? []);
    } catch {}
  }, [id]);

  usePolling(fetchTudo, 5000);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const assumir = async () => {
    setAssumindo(true);
    try {
      const { data } = await api.post(`/conversas/${id}/assumir`);
      setConversa(prev => ({ ...prev, status: data.data?.status ?? 'recepcao' }));
    } catch {
    } finally {
      setAssumindo(false);
    }
  };

  const enviar = async () => {
    const conteudo = texto.trim();
    if (!conteudo) return;
    setTexto('');
    const otimista = {
      id: `tmp-${Date.now()}`,
      tipo: 'recepcao',
      conteudo,
      criado_em: new Date().toISOString(),
    };
    setMensagens(prev => [...prev, otimista]);
    try {
      await api.post('/mensagens', { conversa_id: id, conteudo });
    } catch {
      setMensagens(prev => prev.filter(m => m.id !== otimista.id));
      setTexto(conteudo);
    }
  };

  const podeAssumir = conversa && ['ia_ativa', 'pendente', 'urgente'].includes(conversa.status);
  const assumido = conversa?.status === 'recepcao';
  const acao = ACAO[conversa?.status];
  const slots = conversa?.slots ?? {};

  const topbarSubtitulo = [conversa?.paciente_telefone, conversa?.medico_nome, conversa?.especialidade]
    .filter(Boolean)
    .join(' · ');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      fontFamily: 'Inter, -apple-system, sans-serif',
    }}>
      {/* Topbar */}
      <div style={{
        height: '56px',
        backgroundColor: '#FFFFFF',
        borderBottom: '0.5px solid #E9ECEF',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '0 16px',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate('/fila')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '13px',
            color: '#185FA5',
            cursor: 'pointer',
            padding: '4px 6px',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
          }}
        >
          ← Fila
        </button>

        <div style={{
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          backgroundColor: '#F1F3F5',
          color: '#6C757D',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {iniciais(conversa?.paciente_nome)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0,
            fontSize: '13px',
            fontWeight: 500,
            color: '#1A1A2E',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {conversa?.paciente_nome ?? '—'}
          </p>
          <p style={{
            margin: 0,
            fontSize: '11px',
            color: '#6C757D',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {topbarSubtitulo}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {conversa && <StatusBadge status={conversa.status} />}
          {podeAssumir && (
            <button
              onClick={assumir}
              disabled={assumindo}
              style={{
                padding: '5px 12px',
                backgroundColor: '#185FA5',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: assumindo ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Assumir
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Chat */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          borderRight: '0.5px solid #E9ECEF',
        }}>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}>
            {mensagens.map(m => (
              <ChatBubble key={m.id} mensagem={m} />
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div style={{
            borderTop: '0.5px solid #E9ECEF',
            backgroundColor: '#F8F9FA',
            padding: '10px 12px',
            flexShrink: 0,
          }}>
            {assumido ? (
              <>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#0F6E56' }}>
                  ✓ Você assumiu esta conversa
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={texto}
                    onChange={e => setTexto(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && enviar()}
                    placeholder="Digite uma mensagem..."
                    style={{
                      flex: 1,
                      border: '1px solid #E9ECEF',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      fontSize: '13px',
                      outline: 'none',
                      backgroundColor: '#FFFFFF',
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    onClick={enviar}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#185FA5',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Enviar
                  </button>
                </div>
              </>
            ) : (
              <p style={{ margin: 0, textAlign: 'center', fontSize: '12px', color: '#ADB5BD' }}>
                🤖 IA respondendo automaticamente — clique em "Assumir" para digitar
              </p>
            )}
          </div>
        </div>

        {/* Side panel */}
        <div style={{
          width: '220px',
          flexShrink: 0,
          overflowY: 'auto',
          padding: '14px',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          <SlotPanel slots={slots} />

          <div>
            <p style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#6C757D',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              margin: '0 0 8px',
            }}>
              Paciente
            </p>
            {[
              { label: 'Telefone', value: conversa?.paciente_telefone },
              { label: 'Médico', value: conversa?.medico_nome },
              { label: 'Especialidade', value: conversa?.especialidade },
            ].map(({ label, value }) => (
              <div key={label} style={{ marginBottom: '6px' }}>
                <span style={{ display: 'block', fontSize: '11px', color: '#ADB5BD' }}>{label}</span>
                <span style={{ fontSize: '12px', color: '#1A1A2E' }}>{value ?? '—'}</span>
              </div>
            ))}
          </div>

          {acao && (
            <div style={{
              backgroundColor: acao.bg,
              color: acao.color,
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '12px',
              lineHeight: '1.4',
            }}>
              {acao.icon} {acao.texto}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
